/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import apiClient from 'data/api/apiClient';
import {
    CashRegisterModelsListDto,
    CashRegisterModelsListRequestParams,
    CashRegisterModelsExcelRequestParams,
    CashRegisterModelDetailsDto,
    FactoryNumbersListQueryParams,
    FactoryNumbersListDto,
    CheckSumsListDto,
    StartAttestationDto,
    CompleteAttestationDto,
    AttestationResponseDto,
    VersionsListDto,
    ModelDocumentsListQueryParams,
    ModelDocumentsListDto,
    PresignedFileInfo,
    SubmitDocumentsDto,
    UpdateModelDetailsParams,
    SoftwareVersionItemDto,
} from './cashRegisterModelsClient.typings';
import {
    cutEmptyParams,
    cutEmptyParamsIncludingArrays,
    DATA_UNAVAILABLE,
    makeArrayFromCommaSeparatedString,
} from '../../../utils/utils';
import { PageQueryParams } from '../apiCommon.typings';
import { CashRegisterModelsListTableData } from '../../../components/RegistryWidgets/CashRegisterModels/CashRegisterModelsList/CashRegisterModelsListConfig';

export const SEARCH_TYPE_TO_SEARCH_PARAM: { [key in keyof CashRegisterModelsListTableData]?: string } = {
    name: 'name',
    taxNumber: 'taxNumbers',
};

const getSearchParamName = (searchType: keyof CashRegisterModelsListTableData): string => {
    let searchParam = SEARCH_TYPE_TO_SEARCH_PARAM[searchType];
    if (searchParam === undefined) searchParam = DATA_UNAVAILABLE;
    return searchParam;
};

const adjustCashRegisterModelsListFilterParams = (
    params: CashRegisterModelsListRequestParams | CashRegisterModelsExcelRequestParams,
) => {
    const { search_type, search_text } = params;

    delete params.search_text;
    delete params.search_type;

    let queryParams = {
        ...params,
        [getSearchParamName(search_type as keyof CashRegisterModelsListTableData)]: makeArrayFromCommaSeparatedString(
            search_text,
            true,
        ),
    };

    if (search_type === SEARCH_TYPE_TO_SEARCH_PARAM.name) {
        queryParams = {
            ...queryParams,
            [getSearchParamName(search_type as keyof CashRegisterModelsListTableData)]: search_text,
        };
    }

    return cutEmptyParamsIncludingArrays(queryParams);
};

async function listCashRegistersModels(
    params: CashRegisterModelsListRequestParams,
): Promise<CashRegisterModelsListDto> {
    return apiClient.get<CashRegisterModelsListDto>(
        '1.0.0/cashRegisterModels',
        adjustCashRegisterModelsListFilterParams(params),
    );
}

async function downloadCashRegisterModelsAsExcel(params: CashRegisterModelsExcelRequestParams): Promise<ArrayBuffer> {
    return apiClient.downloadFile('1.0.0/cashRegisterModels/excel', adjustCashRegisterModelsListFilterParams(params));
}

async function fetchCashRegisterModel(taxNumber: string): Promise<CashRegisterModelDetailsDto> {
    return apiClient.get<CashRegisterModelDetailsDto>(`1.0.0/cashRegisterModels/${taxNumber}`);
}

async function updateCashRegisterModel(taxNumber: string, param: UpdateModelDetailsParams): Promise<{}> {
    return apiClient.patch<{}>(`1.0.0/cashRegisterModels/${taxNumber}`, param);
}

async function startAttestation(params: StartAttestationDto): Promise<{}> {
    const { taxNumber, ...requestDto } = params;

    return apiClient.post<{}>(
        `1.0.0/cashRegisterModels/${taxNumber}/startAttestation`,
        cutEmptyParamsIncludingArrays(requestDto),
    );
}

async function completeAttestation(params: CompleteAttestationDto): Promise<AttestationResponseDto> {
    const { taxNumber, ...requestDto } = params;

    return apiClient.post<AttestationResponseDto>(
        `1.0.0/cashRegisterModels/${taxNumber}/endAttestation`,
        cutEmptyParamsIncludingArrays(requestDto),
    );
}

async function getModelSoftwareVersions(taxNumber: string, params: PageQueryParams): Promise<VersionsListDto> {
    return apiClient.get<VersionsListDto>(`1.0.0/cashRegisterModels/${taxNumber}/versions`, cutEmptyParams(params));
}

async function getModelSoftwareVersionNames(taxNumber: string): Promise<SoftwareVersionItemDto[]> {
    return apiClient.get<SoftwareVersionItemDto[]>(`1.0.0/cashRegisterModels/${taxNumber}/versionNames`);
}

async function getVersionCheckSums(
    taxNumber: string,
    versionId: string,
    pageParams: PageQueryParams,
): Promise<CheckSumsListDto> {
    return apiClient.get<CheckSumsListDto>(
        `1.0.0/cashRegisterModels/${taxNumber}/versions/${versionId}/checksums`,
        pageParams,
    );
}

async function eliminateModel(taxNumber: string, comment: string): Promise<{}> {
    return apiClient.post<{}>(`1.0.0/cashRegisterModels/${taxNumber}/eliminate`, { comment });
}

async function activateModel(taxNumber: string, comment: string): Promise<{}> {
    return apiClient.post<{}>(`1.0.0/cashRegisterModels/${taxNumber}/activate`, { comment });
}

async function markVersionOutdated(taxNumber: string, versionId: string): Promise<{}> {
    return apiClient.post<{}>(`1.0.0/cashRegisterModels/${taxNumber}/versions/${versionId}/outdated`);
}

async function activateVersion(taxNumber: string, versionId: string): Promise<{}> {
    return apiClient.post<{}>(`1.0.0/cashRegisterModels/${taxNumber}/versions/${versionId}/activate`);
}

//---for CashRegister (not model) ---//
async function listCashRegistersFactoryNumbers(
    params: FactoryNumbersListQueryParams,
    taxNumber: string,
): Promise<FactoryNumbersListDto> {
    const updatedParams = { ...params };

    if (params.used === 'ALL') {
        delete updatedParams.used;
    }

    return apiClient.get<FactoryNumbersListDto>(
        `1.0.0/cashRegisterModels/${taxNumber}/cashRegisters`,
        cutEmptyParams(updatedParams),
    );
}
//--------------------------------------

function downloadDocument(fileId: string): Promise<ArrayBuffer> {
    return apiClient.downloadFile(`1.0.0/cashRegisterModels/documents/${fileId}`);
}

async function uploadVersionDocument(taxNumber: string, originalFileName: string): Promise<PresignedFileInfo> {
    return apiClient.post<PresignedFileInfo>(`1.0.0/cashRegisterModels/${taxNumber}/documents`, {
        name: originalFileName,
    });
}

async function getModelDocuments(
    taxNumber: string,
    params: ModelDocumentsListQueryParams,
): Promise<ModelDocumentsListDto> {
    return apiClient.get<ModelDocumentsListDto>(
        `1.0.0/cashRegisterModels/${taxNumber}/documents`,
        cutEmptyParams(params),
    );
}

async function uploadDocument(taxNumber: string, name: string): Promise<PresignedFileInfo> {
    return apiClient.post<PresignedFileInfo>(`1.0.0/cashRegisterModels/${taxNumber}/documents`, { name });
}

async function submitDocuments(taxNumber: string, documentsInfo: SubmitDocumentsDto): Promise<{}> {
    return apiClient.post<{}>(
        `1.0.0/cashRegisterModels/${taxNumber}/documents/submit`,
        cutEmptyParamsIncludingArrays(documentsInfo),
    );
}

async function getDocumentUrl(taxNumber: string, id: string): Promise<string> {
    return apiClient.get<string>(`1.0.0/cashRegisterModels/${taxNumber}/documents/${id}/url`);
}

async function deleteDocuments(taxNumber: string, ids: string[]): Promise<{}> {
    return apiClient.delete<{}>(`1.0.0/cashRegisterModels/${taxNumber}/documents`, { ids });
}

export const cashRegisterModelsClient = {
    listCashRegistersModels,
    downloadCashRegisterModelsAsExcel,
    fetchCashRegisterModel,
    updateCashRegisterModel,
    getModelSoftwareVersions,
    getModelSoftwareVersionNames,
    getVersionCheckSums,
    startAttestation,
    completeAttestation,
    eliminateModel,
    activateModel,
    markVersionOutdated,
    activateVersion,
    downloadDocument,
    uploadVersionDocument,
    listCashRegistersFactoryNumbers,
    getModelDocuments,
    uploadDocument,
    submitDocuments,
    getDocumentUrl,
    deleteDocuments,
};
